import { NextRouter } from "next/router"
import { showDialogInfo } from "@/hooks/use-dialog-info"
import { PlaceRef } from "@/types/entity"
import {
  BotGroupChatMessageNotification,
  BotMentionNotification,
  CustomerGroupChatMessageNotification,
  CustomerMentionNotification,
  Notification,
  NotificationGroups,
} from "@/types/notification"
import { NotificationEventData } from "@/types/socket"
import {
  asyncThunkCreator,
  buildCreateSlice,
  PayloadAction,
} from "@reduxjs/toolkit"
import nProgress from "nprogress"

import { notificationClickToAmplitudeProperties } from "@/lib/analytics/amplitude-properties"
import { sendAnalyticsEvent } from "@/lib/analytics/amplitude-utils"
import { downloadDataUri } from "@/lib/download"
import { relativeCanonicalUrl } from "@/lib/entity-utils"
import { links, routes } from "@/lib/routes"
import { openAgentRunReportSheet } from "@/lib/storage/detail-sheet-slice"
import { selectHasItineraryEvents } from "@/lib/storage/itinerary-slice"
import { AppDispatch, RootState } from "@/lib/storage/redux-store"
import {
  removePendingCollectionImport,
  setPendingCollectionImport,
} from "@/lib/storage/saved-slice"
import { setBurgerOpen, setNavValue } from "@/lib/storage/site-nav-slice"
import { tryParseUrl } from "@/lib/url-utils"
import { placeRefFromDetailPageUrl } from "@/lib/utils/routing"
import { showToast } from "@/components/notifications/notification-toast"
import { getInspirationUrl } from "../inspiration"
import { collectionsApi } from "./api/collections-api"
import { itineraryApi } from "./api/itinerary-api"
import { notificationsApi } from "./api/notifications-api"
import { tripsApi } from "./api/trips-api"
import {
  selectDetailSheetOpen,
  selectEntitySheetEntityRef,
} from "./detail-sheet-slice"
import { reloadSavedPlacesList } from "./saved-thunks"
import { openTripSheet } from "./trip-thunks"

const createThunkSlice = buildCreateSlice({
  creators: { asyncThunk: asyncThunkCreator },
})

export type NotificationTabs = "all" | NotificationGroups

export interface NotificationsSliceState {
  currentTab: NotificationTabs
  unreadOnly: boolean
}

export const notificationInitialState: NotificationsSliceState = {
  currentTab: "all",
  unreadOnly: false,
}

function routePathFromNotificationUrl(url: string): string | undefined {
  try {
    const parsed = new URL(url, "https://mindtrip.ai")
    return `${parsed.pathname}${parsed.search}${parsed.hash}`
  } catch {
    return url.startsWith("/") ? url : undefined
  }
}

function isViewingPlaceNotificationTarget(
  place: { entity_ref: PlaceRef },
  state: RootState,
  asPath: string
) {
  const sheetShowingPlace =
    selectDetailSheetOpen(state) &&
    selectEntitySheetEntityRef(state) === place.entity_ref

  return (
    sheetShowingPlace || placeRefFromDetailPageUrl(asPath) === place.entity_ref
  )
}

const notificationsSlice = createThunkSlice({
  name: "notificationsSlice",
  initialState: notificationInitialState,
  reducers: (create) => ({
    setTab: create.reducer((state, action: PayloadAction<NotificationTabs>) => {
      state.currentTab = action.payload
    }),
    setUnreadOnly: create.reducer((state, action: PayloadAction<boolean>) => {
      state.unreadOnly = action.payload
    }),
    receiveNotificationEvent: create.asyncThunk<
      void,
      { data: NotificationEventData; router: NextRouter }
    >(async ({ data: { notification }, router }, thunkAPI) => {
      const dispatch = thunkAPI.dispatch as AppDispatch
      const state = thunkAPI.getState() as RootState

      const groupChatVisible = (
        n:
          | BotGroupChatMessageNotification
          | CustomerGroupChatMessageNotification
          | BotMentionNotification
          | CustomerMentionNotification
      ) => {
        return (
          router.asPath.startsWith("/chat") &&
          state.userSlice.chat.id === n.chat_id &&
          state.userSlice.chat.group
        )
      }

      switch (notification.type) {
        case "group_chat_message":
          // only show toast if we are not in the chat
          if (!groupChatVisible(notification)) {
            showToast(notification, { dispatch, router })
          }
          break
        case "mention":
          // only show toast if we are not in the chat
          if (!groupChatVisible(notification)) {
            showToast(notification, { dispatch, router })
          } else {
            // if we are in group chat they can see this, go ahead and mark read
            void dispatch(
              notificationsApi.endpoints.markRead.initiate(notification.id)
            )
          }
          break
        case "create_collection_from_travel_resource_failed":
          dispatch(setPendingCollectionImport(false))
          if (notification.collection_ref) {
            dispatch(removePendingCollectionImport(notification.collection_ref))
          }
          showToast(notification, { dispatch, router })
          break
        case "create_collection_from_travel_resource_success":
          if (notification.collection) {
            dispatch(
              collectionsApi.util.invalidateTags([
                "Collections",
                "CollectionsStats",
              ])
            )
            dispatch(setPendingCollectionImport(false))
            dispatch(removePendingCollectionImport(notification.collection.ref))
            void dispatch(reloadSavedPlacesList())
          }
          showToast(notification, { dispatch, router })
          break
        case "import_collection_from_google_success":
          if (notification.collection) {
            dispatch(
              collectionsApi.util.invalidateTags([
                "Collections",
                "CollectionsStats",
              ])
            )
            dispatch(setPendingCollectionImport(false))
            dispatch(removePendingCollectionImport(notification.collection.ref))
            void dispatch(reloadSavedPlacesList())
          }
          showToast(notification, { dispatch, router })
          break
        case "import_collection_from_google_failed":
          dispatch(setPendingCollectionImport(false))
          if (notification.collection_ref) {
            dispatch(removePendingCollectionImport(notification.collection_ref))
          }
          showToast(notification, { dispatch, router })
          break
        case "refresh_collection_from_google_success":
          if (notification.collection?.ref) {
            dispatch(removePendingCollectionImport(notification.collection.ref))

            const currentCollectionRef =
              state.collectionSlice.currentCollectionRef
            if (currentCollectionRef === notification.collection.ref) {
              void dispatch(
                collectionsApi.endpoints.getCollection.initiate({
                  ref: currentCollectionRef,
                  page: 1,
                })
              )
            }
            void dispatch(reloadSavedPlacesList())
          }
          showToast(notification, { dispatch, router })
          break
        case "refresh_collection_from_google_failed":
          if (notification.collection_ref) {
            dispatch(removePendingCollectionImport(notification.collection_ref))
          }
          showToast(notification, { dispatch, router })
          break
        case "flight_booking_changed":
        case "flight_booking_confirmed":
          if (notification.trip_ref) {
            dispatch(
              itineraryApi.util.invalidateTags([
                { type: "Itinerary", id: notification.trip_ref },
                { type: "ItineraryEvents", id: notification.trip_ref },
              ])
            )
          }
          dispatch(tripsApi.util.invalidateTags(["TripsCalendar"]))
          showToast(notification, { dispatch, router })
          break
        case "place_review_active":
          showToast(notification, {
            dispatch,
            router,
            showAction:
              !notification.place ||
              !isViewingPlaceNotificationTarget(
                notification.place,
                state,
                router.asPath
              ),
          })
          break
        case "connected_calendar_scan_ready":
          showToast(notification, {
            dispatch,
            router,
            showAction: Boolean(notification.url),
          })
          break
        // for all other notifications, show the toast
        default:
          showToast(notification, { dispatch, router })
          break
      }

      // Invalidate RTK Query cache to refresh notifications list
      dispatch(
        notificationsApi.util.invalidateTags(["Notifications", "UnreadCount"])
      )
    }),

    /**
     * Handles user clicking on a notification.
     * Marks as read and navigates to the appropriate destination.
     */
    handleNotificationAction: create.asyncThunk<
      void,
      {
        notification: Notification
        router: NextRouter
        surface: "toast" | "list"
      }
    >(async ({ notification, router, surface }, thunkAPI) => {
      const dispatch = thunkAPI.dispatch as AppDispatch
      const getState = thunkAPI.getState as () => RootState

      sendAnalyticsEvent({
        name: "NotificationClick",
        properties: notificationClickToAmplitudeProperties(
          notification,
          surface
        ),
      })

      dispatch(setBurgerOpen(false))
      dispatch(setNavValue(""))

      if ("id" in notification && !notification.read_at) {
        void dispatch(
          notificationsApi.endpoints.markRead.initiate(notification.id)
        )
      }

      const chatId =
        notification.type === "mention" ||
        notification.type === "group_chat_message" ||
        notification.type === "folio_document_uploaded_to_chat"
          ? notification.chat_id
          : undefined
      nProgress.start()

      if (chatId) {
        sendAnalyticsEvent({
          name: "ChatSwitch",
          properties: { chat: { id: chatId } },
        })

        await router.push(links.chat(chatId))
      }

      const hasItineraryEvents = selectHasItineraryEvents(getState())

      switch (notification.type) {
        case "comment":
        case "comment_mention":
          await router.push(links.trip(notification.trip_ref))
          await dispatch(
            openTripSheet({
              tripRef: notification.trip_ref,
              eventId: notification.itinerary_event_id,
            })
          )
          break
        case "collaborator_joined":
        case "new_collaboration":
          await router.push(links.trip(notification.trip_ref))
          break
        case "needs_attention_items_ready":
          await router.push(links.trip(notification.trip_ref, "disruptions"))
          break
        case "flight_delay_alert":
          await router.push(links.trip(notification.trip_ref))
          break
        case "folio_document_uploaded_to_chat":
          if (hasItineraryEvents) {
            void dispatch(openTripSheet())
          }
          break
        case "collection_invitation":
          if (notification.invitation_token) {
            await router.push(
              links.joinCollection(notification.invitation_token)
            )
          } else if (notification.collection?.canonical_url) {
            await router.push(
              relativeCanonicalUrl(notification.collection.canonical_url)
            )
          }
          break
        case "new_collection_collaboration":
        case "collection_collaborator_joined":
        case "collection_place_added":
        case "create_collection_from_travel_resource_success":
        case "collection_note_added":
        case "collection_visibility_toggled":
          if (notification.collection?.canonical_url) {
            await router.push(
              relativeCanonicalUrl(notification.collection.canonical_url)
            )
          }
          break
        case "folio_document_upload_failed":
          showDialogInfo({
            title: "Receipt not processed",
            message: (
              <>
                We are having trouble reading the information and cannot process
                your {notification.upload_method}{" "}
                {notification.title ? (
                  <span className="font-medium">{notification.title}</span>
                ) : (
                  ""
                )}
                <span>. Please add the event manually to a desired trip.</span>
              </>
            ),
            okVariant: "primary",
          })
          break
        case "folio_document_uploaded":
          if (!router.pathname.startsWith("/bookings")) {
            await router.push("/bookings")
          } else {
            router.reload()
          }
          break
        case "new_follower":
          if (notification.follower) {
            await router.push(links.profile(notification.follower.handle))
          }
          break
        case "connected_calendar_scan_ready": {
          if (notification.url) {
            const parsed = tryParseUrl(notification.url)
            await router.push(
              parsed?.pathname === "/stories"
                ? `${parsed.pathname}${parsed.search}`
                : "/stories"
            )
          }
          break
        }
        case "report_ready":
          downloadDataUri(notification.download_link, "mindtrip-chats.csv")
          break
        case "inspiration_pdf_ready":
        case "trip_pdf_ready":
          window.open(
            notification.download_link,
            "_blank",
            "noopener,noreferrer"
          )
          break
        case "collections_user_announcement":
        case "collections_creator_announcement":
        case "google_pins_user_announcement":
        case "google_pins_creator_announcement":
          await router.push("/saved?tab=collections")
          break
        case "events_announcement":
          await router.push("/explore?type=event")
          break
        case "inspiration_to_trip":
          if (notification.inspiration) {
            await router.push(getInspirationUrl(notification.inspiration))
          }
          break
        case "inspiration_saved":
          if (notification.inspiration) {
            await router.push(getInspirationUrl(notification.inspiration))
          }
          break
        case "inspiration_published":
          if (notification.inspiration) {
            await router.push(getInspirationUrl(notification.inspiration))
          }
          break
        case "inspiration_item_to_trip":
        case "inspiration_item_saved":
          if (notification.inspiration) {
            await router.push(getInspirationUrl(notification.inspiration))
          }
          break
        case "recommendation_to_booking":
          // TODO should this go to commission page but there is no commission yet.
          break
        case "guide_place_closed":
          if (notification.guide) {
            await router.push(getInspirationUrl(notification.guide))
          }
          break
        case "import_collection_from_google_success":
          if (notification.collection?.canonical_url) {
            await router.push(
              relativeCanonicalUrl(notification.collection.canonical_url)
            )
          }
          break
        case "import_collection_from_google_failed":
        case "create_collection_from_travel_resource_failed":
          // Stay on current page - error message is already shown in the notification
          break
        case "flight_booking_confirmed":
        case "flight_booking_changed":
          if (notification.trip_ref) {
            await router.push(links.trip(notification.trip_ref, "bookings"))
          } else {
            await router.push(
              links.flightBooking(notification.flight_booking_id)
            )
          }
          break
        case "flight_booking_pending_confirm":
        case "flight_booking_ticketing_error":
          await router.push(links.flightBooking(notification.flight_booking_id))
          break
        case "refresh_collection_from_google_success":
          if (notification.collection?.canonical_url) {
            await router.push(
              relativeCanonicalUrl(notification.collection.canonical_url)
            )
          }
          break
        case "refresh_collection_from_google_failed":
          // Stay on current page - error message is already shown in the notification
          break
        case "user_memory_changed":
          sendAnalyticsEvent({
            name: "ClickMemoryToast",
            properties: {
              memory_ref: notification.memory_ref,
              action: notification.action,
              surface,
            },
          })
          await router.push(
            routes.settings.href({
              query: {
                section: "personalization",
                mem: notification.memory_ref,
              },
            })
          )
          break
        case "place_review_active":
          if (notification.place?.canonical_url) {
            await router.push(
              relativeCanonicalUrl(notification.place.canonical_url)
            )
          }
          break
        case "flight_price_alert": {
          const routePath = notification.cta_url
            ? routePathFromNotificationUrl(notification.cta_url)
            : undefined
          sendAnalyticsEvent({
            name: "PriceAlertNotificationClick",
            properties: {
              agent_type: "flight_price_alert",
              source: surface ? `notification_${surface}` : "notification",
              agent_id: notification.proactive_agent_id,
              target_price: notification.target_price,
            },
          })
          if (routePath) {
            await router.push(routePath)
          }
          break
        }
        case "hotel_price_alert": {
          const routePath = notification.cta_url
            ? routePathFromNotificationUrl(notification.cta_url)
            : undefined
          sendAnalyticsEvent({
            name: "PriceAlertNotificationClick",
            properties: {
              agent_type: "hotel_price_alert",
              source: surface ? `notification_${surface}` : "notification",
              agent_id: notification.proactive_agent_id,
              hotel_name: notification.hotel_name,
              target_price: notification.target_price,
              check_in: notification.check_in,
              check_out: notification.check_out,
              room_selection: notification.room_name
                ? "single_room"
                : "any_room",
              room_type_count: notification.room_name ? 1 : 0,
            },
          })
          if (routePath) {
            await router.push(routePath)
          }
          break
        }
        case "custom_agent_report":
          void dispatch(
            openAgentRunReportSheet({
              runRef: notification.proactive_agent_run_ref,
              agentName: notification.agent_name,
            })
          )
          break
        default:
          break
      }

      nProgress.done()
    }),
  }),
  selectors: {
    selectTab(state) {
      return state.currentTab
    },
    selectUnreadOnly(state) {
      return state.unreadOnly
    },
  },
})

export const {
  setTab,
  setUnreadOnly,
  receiveNotificationEvent,
  handleNotificationAction,
} = notificationsSlice.actions

export const { selectTab, selectUnreadOnly } = notificationsSlice.selectors

export default notificationsSlice
